import { useLayoutEffect, useMemo, useRef } from 'react'
import Konva from 'konva'
import { Group, Rect, Text } from 'react-konva'
import { VisualShape } from 'app/frontend/types/custom/VisualPlan'
import {
  DEFAULT_LABEL_FONT_SIZE,
  DEFAULT_TEXT_BACKGROUND,
  DEFAULT_TEXT_COLOR,
  TEXT_FONT_FAMILY,
  TEXT_LINE_HEIGHT,
  TEXT_PADDING_X,
  TEXT_PADDING_Y,
} from '../constants'
import { shapeDisplayLabel } from './shapeLabels'
import { isAdditiveClick } from './shapeInteraction'

const LABEL_GAP = -5

export type LabelInteraction = {
  onSelect: (additive?: boolean) => void
  onMouseEnter?: () => void
  onMouseLeave?: () => void
  draggable?: boolean
  onOffsetChange?: (offset: { x: number; y: number }) => void
}

type Props = {
  shape: VisualShape
  width: number
  height: number
  getNode?: () => Konva.Node | null
  interaction?: LabelInteraction
}

type Aabb = { x: number; y: number; width: number; height: number }

let measurer: Konva.Text | null = null

const measureLabelBox = (text: string, fontSize: number) => {
  if (!measurer) measurer = new Konva.Text({ listening: false })
  measurer.setAttrs({
    text,
    fontSize,
    fontFamily: TEXT_FONT_FAMILY,
    lineHeight: TEXT_LINE_HEIGHT,
    wrap: 'none',
    padding: 0,
  })
  const contentWidth = Math.max(1, measurer.getTextWidth())
  const contentHeight = Math.max(measurer.height(), fontSize * TEXT_LINE_HEIGHT)
  return {
    width: contentWidth + TEXT_PADDING_X * 2,
    height: contentHeight + TEXT_PADDING_Y * 2,
  }
}

const rotatePoint = (
  x: number,
  y: number,
  cx: number,
  cy: number,
  deg: number,
) => {
  if (!deg) return { x, y }
  const rad = (deg * Math.PI) / 180
  const cos = Math.cos(rad)
  const sin = Math.sin(rad)
  const dx = x - cx
  const dy = y - cy
  return { x: cx + dx * cos - dy * sin, y: cy + dx * sin + dy * cos }
}

const aabbFromPoints = (points: { x: number; y: number }[]): Aabb => {
  const xs = points.map((point) => point.x)
  const ys = points.map((point) => point.y)
  const minX = Math.min(...xs)
  const minY = Math.min(...ys)
  const maxX = Math.max(...xs)
  const maxY = Math.max(...ys)
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY }
}

const getShapeAabb = (
  shape: VisualShape,
  width: number,
  height: number,
): Aabb => {
  const cx = shape.x * width
  const cy = shape.y * height
  const rot = shape.rotation ?? 0

  if (
    shape.type === 'rect' ||
    shape.type === 'triangle' ||
    shape.type === 'icon'
  ) {
    const hw = (shape.width * width) / 2
    const hh = (shape.height * height) / 2
    return aabbFromPoints([
      rotatePoint(cx - hw, cy - hh, cx, cy, rot),
      rotatePoint(cx + hw, cy - hh, cx, cy, rot),
      rotatePoint(cx + hw, cy + hh, cx, cy, rot),
      rotatePoint(cx - hw, cy + hh, cx, cy, rot),
    ])
  }

  if (shape.type === 'circle') {
    const rx = (shape.radius_x ?? shape.radius ?? 0.08) * width
    const ry = (shape.radius_y ?? shape.radius ?? 0.08) * width
    return aabbFromPoints([
      rotatePoint(cx - rx, cy - ry, cx, cy, rot),
      rotatePoint(cx + rx, cy - ry, cx, cy, rot),
      rotatePoint(cx + rx, cy + ry, cx, cy, rot),
      rotatePoint(cx - rx, cy + ry, cx, cy, rot),
    ])
  }

  if (shape.type === 'text') {
    return { x: cx, y: cy, width: 0, height: 0 }
  }

  const pts: { x: number; y: number }[] = []
  for (let i = 0; i < shape.points.length; i += 2) {
    pts.push(
      rotatePoint(
        cx + shape.points[i] * width,
        cy + shape.points[i + 1] * height,
        cx,
        cy,
        rot,
      ),
    )
  }
  return pts.length > 0
    ? aabbFromPoints(pts)
    : { x: cx, y: cy, width: 0, height: 0 }
}

const resolveAnchor = (
  node: Konva.Node | null,
  fallbackX: number,
  fallbackY: number,
) => {
  if (node) {
    const layer = node.getLayer()
    const rect = node.getClientRect({
      skipShadow: true,
      relativeTo: layer ?? undefined,
    })
    return { x: rect.x + rect.width / 2, y: rect.y + rect.height }
  }
  return { x: fallbackX, y: fallbackY }
}

const ShapeLabel = ({ shape, width, height, getNode, interaction }: Props) => {
  const groupRef = useRef<Konva.Group>(null)
  const labelText = shape.name?.trim() || shapeDisplayLabel(shape)
  const fontSize = (shape.label_font_size ?? DEFAULT_LABEL_FONT_SIZE) * width
  const box = measureLabelBox(labelText, fontSize)
  const fallback = useMemo(
    () => getShapeAabb(shape, width, height),
    [shape, width, height],
  )
  const fallbackX = fallback.x + fallback.width / 2
  const fallbackY = fallback.y + fallback.height
  const offsetXPx = (shape.label_offset_x ?? 0) * width
  const offsetYPx = (shape.label_offset_y ?? 0) * height

  useLayoutEffect(() => {
    const node = getNode?.() ?? null
    const reposition = () => {
      const group = groupRef.current
      if (!group || group.isDragging()) return
      const anchor = resolveAnchor(node, fallbackX, fallbackY)
      group.x(anchor.x + offsetXPx)
      group.y(anchor.y + offsetYPx)
      group.getLayer()?.batchDraw()
    }

    reposition()
    if (!node) return
    node.on('dragmove.shapeLabel transform.shapeLabel', reposition)
    return () => {
      node.off('.shapeLabel')
    }
  }, [
    getNode,
    fallbackX,
    fallbackY,
    offsetXPx,
    offsetYPx,
    labelText,
    fontSize,
    box.width,
    box.height,
  ])

  const handleDragEnd = () => {
    const group = groupRef.current
    if (!group) return
    const anchor = resolveAnchor(getNode?.() ?? null, fallbackX, fallbackY)
    interaction?.onOffsetChange?.({
      x: (group.x() - anchor.x) / width,
      y: (group.y() - anchor.y) / height,
    })
  }

  return (
    <Group
      ref={groupRef}
      x={fallbackX + offsetXPx}
      y={fallbackY + offsetYPx}
      listening={interaction !== undefined}
      draggable={interaction?.draggable ?? false}
      onMouseDown={(event) => interaction?.onSelect(isAdditiveClick(event.evt))}
      onTap={() => interaction?.onSelect()}
      onMouseEnter={interaction?.onMouseEnter}
      onMouseLeave={interaction?.onMouseLeave}
      onDragEnd={handleDragEnd}
    >
      <Rect
        x={-box.width / 2}
        y={LABEL_GAP}
        width={box.width}
        height={box.height}
        fill={DEFAULT_TEXT_BACKGROUND}
        cornerRadius={4}
      />
      <Text
        x={-box.width / 2 + TEXT_PADDING_X}
        y={LABEL_GAP + TEXT_PADDING_Y}
        width={Math.max(1, box.width - TEXT_PADDING_X * 2)}
        text={labelText}
        fontSize={fontSize}
        fontFamily={TEXT_FONT_FAMILY}
        lineHeight={TEXT_LINE_HEIGHT}
        fill={DEFAULT_TEXT_COLOR}
        align='center'
        wrap='none'
        listening={false}
      />
    </Group>
  )
}

export default ShapeLabel
